import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";
import { Users, Copy, Gift, TrendingUp, DollarSign, CheckCircle, Search, Link2, Share2 } from "lucide-react";

const ReferralPage = () => {
  const [tab, setTab] = useState("apply"); // apply, check
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [checkEmail, setCheckEmail] = useState("");

  const handleApply = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/apply`, { email: email.trim(), name: name.trim() });
      setResult(res.data);
      toast.success("Referral link created!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const handleCheck = async () => {
    if (!checkEmail.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referral/check/${encodeURIComponent(checkEmail.trim())}`);
      setStats(res.data);
    } catch { toast.error("No referral found for this email"); }
    finally { setLoading(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied!");
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="referral-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Gift className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Refer & Earn 30%</h1>
            <p className="text-gray-500 max-w-md mx-auto">Share your referral link with friends. When they purchase and get approved, you earn 30% commission!</p>
          </div>

          {/* How it works */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <Card className="bg-green-50 border-green-200 text-center">
              <CardContent className="p-4">
                <Share2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-900">1. Share</p>
                <p className="text-xs text-gray-500">Share your unique link</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200 text-center">
              <CardContent className="p-4">
                <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-900">2. Friend Buys</p>
                <p className="text-xs text-gray-500">They purchase a package</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200 text-center">
              <CardContent className="p-4">
                <DollarSign className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-900">3. You Earn</p>
                <p className="text-xs text-gray-500">Get 30% commission</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button variant={tab === "apply" ? "default" : "outline"} className={tab === "apply" ? "bg-green-600 text-white" : ""} onClick={() => { setTab("apply"); setStats(null); }} data-testid="tab-apply">
              <Link2 className="h-4 w-4 mr-1" /> Get Referral Link
            </Button>
            <Button variant={tab === "check" ? "default" : "outline"} className={tab === "check" ? "bg-blue-600 text-white" : ""} onClick={() => { setTab("check"); setResult(null); }} data-testid="tab-check">
              <Search className="h-4 w-4 mr-1" /> Check Earnings
            </Button>
          </div>

          {/* Apply Tab */}
          {tab === "apply" && (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6 space-y-4">
                {!result ? (
                  <>
                    <div className="space-y-2">
                      <Label>Your Name</Label>
                      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" data-testid="ref-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Your Email</Label>
                      <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" data-testid="ref-email" />
                      <p className="text-xs text-gray-500">We'll notify you here when you earn commissions</p>
                    </div>
                    <Button onClick={handleApply} disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full h-11 font-bold" data-testid="ref-apply-btn">
                      {loading ? "Creating..." : "Get My Referral Link"}
                    </Button>
                  </>
                ) : (
                  <div className="text-center space-y-4">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                    <h3 className="text-lg font-bold text-gray-900">{result.message}</h3>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <p className="text-xs text-gray-500 mb-2">Your Referral Link</p>
                      <div className="flex items-center gap-2">
                        <input value={result.ref_link} readOnly className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" data-testid="ref-link" />
                        <Button size="sm" variant="outline" onClick={() => copyLink(result.ref_link)} data-testid="ref-copy"><Copy className="h-4 w-4" /></Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Code: <span className="font-mono font-bold text-green-600">{result.ref_code}</span></p>
                    </div>
                    <Button variant="outline" onClick={() => setResult(null)}>Create Another</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Check Earnings Tab */}
          {tab === "check" && (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6 space-y-4">
                {!stats ? (
                  <>
                    <div className="space-y-2">
                      <Label>Your Email</Label>
                      <Input type="email" value={checkEmail} onChange={e => setCheckEmail(e.target.value)} placeholder="Email you used to register" data-testid="check-email" onKeyDown={e => e.key === "Enter" && handleCheck()} />
                    </div>
                    <Button onClick={handleCheck} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 font-bold" data-testid="check-btn">
                      {loading ? "Checking..." : "Check My Earnings"}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <Users className="h-5 w-5 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-extrabold text-gray-900">{stats.total_referrals}</p>
                        <p className="text-xs text-gray-500">Referrals</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <DollarSign className="h-5 w-5 text-amber-600 mx-auto mb-1" />
                        <p className="text-2xl font-extrabold text-gray-900">${stats.total_earnings}</p>
                        <p className="text-xs text-gray-500">Total Earned</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <TrendingUp className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                        <p className="text-2xl font-extrabold text-gray-900">${stats.pending_earnings}</p>
                        <p className="text-xs text-gray-500">Pending</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Your link:</span>
                      <span className="text-xs font-mono text-green-600 flex-1 truncate">{stats.ref_link}</span>
                      <Button size="sm" variant="ghost" onClick={() => copyLink(stats.ref_link)}><Copy className="h-3 w-3" /></Button>
                    </div>
                    {stats.history?.length > 0 && (
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm mb-2">Commission History</h4>
                        {stats.history.map((h, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{h.package_name}</p>
                              <p className="text-xs text-gray-500">{new Date(h.created_at).toLocaleDateString()}</p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">+{h.currency} {(h.amount * 0.3).toFixed(2)}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button variant="outline" onClick={() => setStats(null)} className="w-full">Check Another Email</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReferralPage;
